// Письма канала почты (008: spec FR-016 – FR-027в; data-model.md, «Сообщение», «Событие
// платформы», «Ответное письмо»; contracts/api.md, «Схемы»). Статус меняют отчёты о доставке
// по рангам (research, Р4): queued < sent, sent_unknown < delivered < failed.
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';

// 033: shadow — письмо о пунктах собрано, но не уходит (рассылка протокола «в тени» или адрес вне
// списка песочницы; research Р2 033). Конечное состояние без ошибки и попыток.
export const mailMessageStates = [
  'queued',
  'sent',
  'sent_unknown',
  'delivered',
  'failed',
  'shadow',
] as const;
export type MailMessageState = (typeof mailMessageStates)[number];
export const MailMessageStateSchema = z.enum(mailMessageStates);

export const mailMessageStateLabels = {
  queued: 'В очереди',
  sent: 'Отправлено',
  sent_unknown: 'Отправлено, доставка неизвестна',
  delivered: 'Доставлено',
  failed: 'Ошибка',
  shadow: 'Тень — не отправлено',
} as const satisfies Record<MailMessageState, string>;

/** Ранг статуса: отчёт с рангом не выше текущего статус не меняет (FR-026). */
export const mailMessageStateRank = {
  queued: 0,
  sent: 1,
  sent_unknown: 1,
  delivered: 2,
  failed: 3,
  // Тень не уходит: отчёт канала её не меняет.
  shadow: 3,
} as const satisfies Record<MailMessageState, number>;

/**
 * Вид строки списка: письмо (в 008 — пробное) или ответное письмо на него. 020: письма доступа
 * к протоколу — «Вам открыли доступ» и приглашение (contracts/api.md 020, «Письма»). 025: письмо
 * восстановления пароля (contracts/api.md 025, «Письма»).
 */
export const mailMessageKinds = [
  'test',
  'reply',
  'access_granted',
  'access_invite',
  'password_recovery',
  // 030 (contracts/api.md 030, «Письма»): «Первое письмо», копия куратору внешнего, письмо
  // со ссылкой входа по запросу.
  'first_letter',
  'first_letter_copy',
  'login_link',
  // 031 (contracts/api.md 031, «Письма»): письма ритма планёрки и копия куратору внешнего.
  'meeting_results',
  'meeting_tomorrow',
  'morning',
  'item_letter_copy',
  // 032 (contracts/api.md 032, §6): письмо руководителю — копии по его людям, одно в день.
  'manager_copy',
  // 035 (contracts/api.md 035): приветственное письмо пользователю, которого завёл администратор.
  'welcome',
] as const;
export type MailMessageKind = (typeof mailMessageKinds)[number];
export const MailMessageKindSchema = z.enum(mailMessageKinds);

export const mailMessageKindLabels = {
  test: 'Пробное',
  reply: 'Ответное',
  access_granted: 'Доступ к протоколу',
  access_invite: 'Приглашение в протокол',
  password_recovery: 'Восстановление пароля',
  first_letter: 'Первое письмо',
  first_letter_copy: 'Копия «Первого письма» куратору',
  login_link: 'Ссылка входа',
  meeting_results: 'Итоги планёрки',
  meeting_tomorrow: 'Завтра планёрка',
  morning: 'Утреннее письмо',
  item_letter_copy: 'Копия письма о пунктах куратору',
  manager_copy: 'Копия руководителю',
  welcome: 'Приветственное письмо',
} as const satisfies Record<MailMessageKind, string>;

/** Письмо ждёт без попыток (research, Р6). 033: stopped — «Остановить все сообщения» (FR-007). */
export const mailWaitingReasons = [
  'sending_disabled',
  'connection_error',
  'quota',
  'stopped',
] as const;
export type MailWaitingReason = (typeof mailWaitingReasons)[number];

export const mailWaitingLabels = {
  sending_disabled: 'ждёт: отправка отключена',
  connection_error: 'ждёт исправления подключения',
  quota: 'ждёт сброса предела Resend',
  stopped: 'ждёт: сообщения остановлены',
} as const satisfies Record<MailWaitingReason, string>;

/**
 * Письма о пунктах (033: FR-003, data-model.md, «Правило письма»): подчиняются песочнице и тени.
 * Остальные виды — по запросу человека и о доступе (FR-006): песочнице не подчиняются. 031
 * добавит письма ритма планёрки.
 */
export const ITEM_LETTER_KINDS: readonly MailMessageKind[] = [
  'first_letter',
  'first_letter_copy',
  // 031: письма ритма планёрки.
  'meeting_results',
  'meeting_tomorrow',
  'morning',
  'item_letter_copy',
  // 032: копия руководителю — песочница, тень, стоп-кран, тихие часы, как письма ритма.
  'manager_copy',
];

/** Вариант шаблона (FR-029). */
export const mailVariants = ['notice', 'full'] as const;
export type MailVariant = (typeof mailVariants)[number];

/** События Resend, на которые сервис регистрирует отчёты (research, Р4). */
export const mailEventTypes = [
  'email.sent',
  'email.delivered',
  'email.delivery_delayed',
  'email.bounced',
  'email.complained',
  'email.failed',
  'email.suppressed',
  'email.received',
] as const;
export type MailEventType = (typeof mailEventTypes)[number];

export const mailEventLabels = {
  'email.sent': 'Принято Resend',
  'email.delivered': 'Доставлено',
  'email.delivery_delayed': 'Доставка задерживается',
  'email.bounced': 'Отклонено сервером адресата',
  'email.complained': 'Жалоба на спам',
  'email.failed': 'Не отправлено',
  'email.suppressed': 'Адрес подавлен Resend',
  'email.received': 'Получено ответное письмо',
} as const satisfies Record<MailEventType, string>;

/** Ответное письмо длиннее — обрезается с пометкой (FR-027а). */
export const REPLY_TEXT_MAX = 100_000;

export const MailMessageReasonSchema = z.object({ code: z.string(), text: z.string() });

/**
 * 0.26.1 (ACC-07): почему письмо стало тенью — каждая причина отдельно: рассылка протокола
 * выключена, протокол в тени, протокола больше нет, адрес вне списка песочницы, адрес копии
 * основному куратору вне списка (письмо внешнему в песочнице — только с копией, принцип IV).
 * 035 (FR-011; research Р1, Р2): приветственное письмо не уходит — есть более новое письмо
 * (replaced), ссылку выпустили вручную после сборки письма (manual_link), пароль уже задан
 * (password_set). Исправлять у этих причин нечего.
 */
export const shadowReasonCodes = [
  'mailing_off',
  'mailing_shadow',
  'protocol_gone',
  'not_in_sandbox',
  'copy_not_in_sandbox',
  'replaced',
  'manual_link',
  'password_set',
] as const;
export type ShadowReasonCode = (typeof shadowReasonCodes)[number];
export const ShadowReasonCodeSchema = z.enum(shadowReasonCodes);

export const shadowReasonLabels = {
  mailing_off: 'Рассылка протокола выключена',
  mailing_shadow: 'Рассылка протокола в тени',
  protocol_gone: 'Протокола больше нет',
  not_in_sandbox: 'Адреса нет в списке песочницы',
  copy_not_in_sandbox: 'Адреса копии основному куратору нет в списке песочницы',
  replaced: 'Заменено новым письмом',
  manual_link: 'Ссылку передали вручную',
  password_set: 'Пароль уже задан',
} as const satisfies Record<ShadowReasonCode, string>;

/** Причина тени на момент сборки (build) или попытки отправки (send); не переписывается. */
export const ShadowReasonSchema = z.object({
  code: ShadowReasonCodeSchema,
  stage: z.enum(['build', 'send']),
  at: TimestampSchema,
  /** Протокол причины — у рассылки протокола; номер для подписи «П-12». */
  documentId: IdSchema.optional(),
  documentNumber: z.number().int().min(1).optional(),
});
export type ShadowReason = z.infer<typeof ShadowReasonSchema>;

/** Причина, которая действует сейчас: код и протокол (у причин рассылки). */
export const ShadowFactSchema = z.object({
  code: ShadowReasonCodeSchema,
  documentId: IdSchema.optional(),
  documentNumber: z.number().int().min(1).optional(),
});
export type ShadowFact = z.infer<typeof ShadowFactSchema>;

/**
 * Карточка тени (ACC-07): причины по истории; какие из них действуют сейчас — по каждому
 * протоколу письма отдельно (ревью W5: у письма из нескольких протоколов статус П-12 не берётся
 * от П-14); где исправить — переход, доступный пользователю, или кто может исправить.
 */
export const ShadowStatusSchema = z.object({
  reasons: z.array(ShadowReasonSchema),
  current: z.array(ShadowFactSchema),
  fix: z.array(
    ShadowFactSchema.extend({
      /** Внутренний переход к месту исправления; нет — места у пользователя нет. */
      link: z.string().nullable(),
      /** Кто может исправить, если перехода нет: «администратор клиента», «ведущий П-12». */
      who: z.string().nullable(),
    }),
  ),
});
export type ShadowStatus = z.infer<typeof ShadowStatusSchema>;

export const MailMessageRowSchema = z.object({
  id: IdSchema,
  /** Письмо, к которому строка относится: у письма — оно само, у ответного — исходное. */
  messageId: IdSchema,
  number: z.number().int().min(1),
  kind: MailMessageKindSchema,
  /** Адрес; сопровождению без доступа — ***@домен, у чужого ответного письма — пусто (FR-022). */
  recipient: z.string(),
  subject: z.string(),
  /** У ответного письма статуса нет. */
  state: MailMessageStateSchema.nullable(),
  waiting: z.enum(mailWaitingReasons).nullable(),
  reason: MailMessageReasonSchema.nullable(),
  createdAt: TimestampSchema,
  replies: z.number().int().min(0),
  /** 033 (FR-004): письмо о пунктах ушло в песочнице — пометка «Песочница». */
  sandbox: z.boolean(),
});
export type MailMessageRow = z.infer<typeof MailMessageRowSchema>;

export const MailEventSchema = z.object({
  type: z.enum(mailEventTypes),
  at: TimestampSchema,
  /** Тип и подтип отказа, слова платформы — без адресов. */
  details: z.record(z.string(), z.string()),
});

export const MailReplySchema = z.object({
  id: IdSchema,
  /** Сопровождению без доступа на чужое письмо — пусто вместе с темой и текстом (FR-022). */
  from: z.string(),
  subject: z.string(),
  text: z.string(),
  truncated: z.boolean(),
  attachments: z.array(z.object({ name: z.string(), size: z.number().int().min(0) })),
  receivedAt: TimestampSchema,
  hidden: z.boolean(),
  /** 033 (FR-004): ответ на письмо песочницы — «проверка», пункт не меняет. */
  test: z.boolean(),
});
export type MailReply = z.infer<typeof MailReplySchema>;

export const MailMessageCardSchema = MailMessageRowSchema.extend({
  createdBy: PersonRefSchema.nullable(),
  variant: z.enum(mailVariants),
  /** Текст письма — как ушёл (ADR-0006). */
  text: z.string(),
  events: z.array(MailEventSchema),
  replyRows: z.array(MailReplySchema),
  operation: z.string().nullable(),
  /** 0.26.1 (ACC-07): письмо в тени — причины, что действует сейчас, где исправить. */
  shadow: ShadowStatusSchema.optional(),
  /**
   * 0.26.1 (ACC-01): части письма руководителю по протоколам — сколько копий и ответов каждого
   * протокола; исключённая при отправке часть — с причиной и временем (рассылка выключена, в тени,
   * протокола нет). Нет — письмо не руководителю или собрано до 0.26.1.
   */
  parts: z
    .array(
      z.object({
        documentId: IdSchema,
        documentNumber: z.number().int().min(1).nullable(),
        copies: z.number().int().min(0),
        answered: z.number().int().min(0),
        excluded: z
          .object({
            reason: z.enum(['mailing_off', 'mailing_shadow', 'protocol_gone']),
            at: TimestampSchema,
          })
          .nullable(),
      }),
    )
    .optional(),
});
export type MailMessageCard = z.infer<typeof MailMessageCardSchema>;
